import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

/**
 * ImageCropModal — presents an image to the user with an interactive crop
 * frame (zoom + pan), returns a cropped dataURL via `onConfirm`.
 *
 * Props:
 *   open, onClose
 *   src          — data URL of the source image
 *   aspect       — crop aspect ratio (1 for square, 16/9 for cover, etc.)
 *   cropShape    — "rect" | "round"
 *   outputMime   — "image/jpeg" | "image/png" (default "image/jpeg")
 *   outputQuality — 0..1 (default 0.9)
 *   maxOutputPx  — longest edge clamp after crop (default 1200)
 *   onConfirm(dataUrl)
 *   title, description
 */
export function ImageCropModal({
  open,
  onClose,
  src,
  aspect = 1,
  cropShape = "rect",
  outputMime = "image/jpeg",
  outputQuality = 0.9,
  maxOutputPx = 1200,
  onConfirm,
  title = "Crop image",
  description = "Drag to reposition, use the slider to zoom.",
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open, src]);

  const onCropComplete = useCallback((_area, areaPx) => {
    setCroppedAreaPixels(areaPx);
  }, []);

  const confirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const dataUrl = await cropImage(src, croppedAreaPixels, { outputMime, outputQuality, maxOutputPx });
      onConfirm(dataUrl);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="image-crop-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="relative w-full h-[320px] bg-black/90 rounded-md overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
            />
          )}
        </div>

        <div className="px-1 pt-2">
          <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground mb-2">
            <span>Zoom</span>
            <span>{zoom.toFixed(1)}x</span>
          </div>
          <Slider
            value={[zoom]}
            min={1}
            max={4}
            step={0.05}
            onValueChange={(v) => setZoom(v[0])}
            data-testid="image-crop-zoom-slider"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="image-crop-cancel">Cancel</Button>
          <Button
            onClick={confirm}
            disabled={!croppedAreaPixels || processing}
            className="bg-brand-orange hover:bg-[#E65C00] text-white"
            data-testid="image-crop-confirm"
          >
            {processing ? "Processing…" : "Apply crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- helpers --------------------------------------------------------------

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function cropImage(src, area, { outputMime, outputQuality, maxOutputPx }) {
  const img = await loadImage(src);
  const { width: w, height: h, x, y } = area;
  // Clamp the output size so logos don't end up as 4000px blobs.
  const scale = Math.min(1, maxOutputPx / Math.max(w, h));
  const ow = Math.round(w * scale);
  const oh = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = ow;
  canvas.height = oh;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, x, y, w, h, 0, 0, ow, oh);
  return canvas.toDataURL(outputMime, outputQuality);
}

/** Convert a File / Blob to a data URL (used before opening the crop modal). */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
